import { StatusTabs } from "../components/StatusTabs";
import { StatusTopBar } from "../components/StatusTopBar";
import changelogData from "../data/changelog.json";

interface ChangelogEntry {
  id: string;
  date: string;
  type: "Feature" | "Fix" | "Security";
  title: string;
  description: string;
}

const entries = changelogData as ChangelogEntry[];

function formatChangelogDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const TYPE_STYLES: Record<ChangelogEntry["type"], string> = {
  Feature: "bg-dr-green/10 text-dr-green border-dr-green/20",
  Fix: "bg-status-partial/10 text-status-partial border-status-partial/20",
  Security: "bg-status-major/10 text-status-major border-status-major/20",
};

export function Changelog(): JSX.Element {
  return (
    <div className="mx-auto max-w-3xl px-6 py-12 md:py-20">
      <StatusTopBar />
      <StatusTabs />

      <div className="mb-12">
        <h1 className="text-3xl font-bold tracking-tight text-dr-heading">Changelog</h1>
        <p className="mt-2 text-text-secondary">
          Restez informé des dernières mises à jour, nouveautés et correctifs de la plateforme.
        </p>
      </div>

      <div className="space-y-12">
        {entries.length === 0 ? (
          <p className="text-text-muted">Aucun log disponible pour le moment.</p>
        ) : (
          entries.map((entry) => (
            <article key={entry.id} className="relative flex flex-col gap-3 md:flex-row md:gap-8">
              {/* Date column */}
              <div className="shrink-0 md:w-32">
                <time
                  dateTime={entry.date}
                  className="sticky top-6 text-sm font-medium text-text-muted"
                >
                  {formatChangelogDate(entry.date)}
                </time>
              </div>

              {/* Content column */}
              <div className="flex-1 rounded-xl border border-white/[0.05] bg-white/[0.02] p-6 shadow-sm transition-colors hover:border-white/[0.08] hover:bg-white/[0.04]">
                <div className="mb-3 flex items-center gap-3">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                      TYPE_STYLES[entry.type] || "bg-white/10 text-text-muted"
                    }`}
                  >
                    {entry.type}
                  </span>
                  <h2 className="text-lg font-bold text-dr-heading">{entry.title}</h2>
                </div>
                <div className="prose prose-sm prose-invert max-w-none text-text-secondary">
                  <p className="leading-relaxed">{entry.description}</p>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
